import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeftIcon, CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';
import { offerSigned } from './welcomeSteps';
import { useAuth } from '../../context/AuthContext';

const ROLE_LABELS = {
  admin: 'Office Admin',
  teacher: 'Full-Time Teacher',
  float: 'Part-Time Float Teacher',
  cook: 'Cook / Food Services',
};

export default function MyOfferLetterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerSig, setOfferSig] = useState('');
  const [idFile, setIdFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/my/offer-letter')
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load your offer letter.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  if (!data) return null;

  // The offer letter is signed when ITS timestamp is set. This waited on
  // handbook_acknowledged_at too — a field nothing writes since 2026-09-14 — so
  // a person whose offer was recorded was shown the signing form, and the ID
  // upload, again (corrected 2026-10-31; see welcomeSteps.js).
  const alreadySigned = offerSigned(data);
  const roleLabel = ROLE_LABELS[data.role] || data.role;
  const expectedName = `${data.first_name} ${data.last_name}`;
  const today_str = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Format a YYYY-MM-DD column value as a long-form local date without
  // UTC rollback (same fix as src/utils/generateOfferLetter.js formatDate).
  const formatDay = (d) => {
    if (!d) return null;
    const s = typeof d === 'string' ? d.slice(0, 10) : null;
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, day] = s.split('-').map(Number);
      return new Date(y, m - 1, day, 12).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const startStr = formatDay(data.offer_start_date) || 'to be determined';
  const firstPayStr = formatDay(data.first_pay_date) || '_____________';
  const responsibilities = Array.isArray(data.role_responsibilities) && data.role_responsibilities.length === 4
    ? data.role_responsibilities
    : ['', '', '', ''];

  const submit = async () => {
    // ONE SIGNATURE, ONE FACT. The handbook acknowledgement left this flow on
    // 2026-09-19 — it is its own record, against its own version, on
    // /my/policies. Gating the offer letter on it would also have been a
    // latent lockout: the field is gone, so `handbookRequired` could only ever
    // have blocked a signature nobody could give.
    if (!offerSig.trim()) {
      return toast.error('Please sign the offer letter.');
    }
    if (!idFile) {
      return toast.error('Please upload a photo or scan of your state-issued ID before signing.');
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('offer_signature', offerSig);
      fd.append('state_id', idFile);
      await api.post('/my/offer-letter/sign', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Offer accepted!');
      navigate('/my', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not record signatures.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-2xl mx-auto">
        <Link to="/my" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </Link>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-orange-50 border-b border-orange-100 p-5 flex items-center gap-3">
            <DocumentTextIcon className="w-6 h-6 text-orange-500" />
            <h1 className="text-xl font-semibold text-gray-900">Conditional Offer Letter</h1>
          </div>

          {/* Letter content — inline render mirrors generateOfferLetter.js so
              the screen text matches the attached PDF exactly. */}
          <div className="p-8 prose prose-sm max-w-none">
            {/* Letterhead */}
            <div className="flex items-start gap-4 mb-6 not-prose">
              <img src="https://booking.wearebbc.com/BBC_ICON.png" alt="BBC" className="w-16 h-16 rounded-lg" />
              <div className="flex-1 text-right text-xs text-gray-600 leading-relaxed">
                <p className="font-semibold text-gray-900 mb-1">Ramal Taylor, Executive Director</p>
                <p>1818 S Main St, South Bend IN 46613</p>
                <p>Phone: (574) 855-1604  |  Fax: (574) 855-1754</p>
                <p>Email: director@wearebbc.com</p>
                <p>Website: wearebbc.com</p>
              </div>
            </div>

            <p>{today_str}</p>
            <p>Dear {data.first_name},</p>
            <p>
              Congratulations and welcome to the Bright Beginnings Family! We're so excited to have you
              join our team as our new <strong>{roleLabel}</strong>. Your passion for children, positive energy,
              and dedication to learning make you a perfect fit for BBC.
            </p>
            <p>
              Your official start date is <strong>{startStr}</strong>. In this role, you'll help carry out our mission
              by creating a safe, engaging, and nurturing environment for our children, building strong
              partnerships with families, and supporting our team's growth and success.
            </p>
            <p>In this role, you will:</p>
            <ul>
              {responsibilities.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            <p>
              Your pay rate will be <strong>${data.offer_salary || '_____'} per hour</strong>, deposited weekly on
              Fridays, beginning <strong>{firstPayStr}</strong>. This is an at-will position, meaning either you or
              BBC may end the employment relationship at any time, for any reason, with or without notice.
            </p>

            <h3 className="text-orange-600 mt-6">Pre-Employment Requirements</h3>
            <p>
              This offer is contingent upon successful completion of the following screenings and trainings,
              along with satisfactory background check results:
            </p>
            <ul>
              <li>Criminal History Check</li>
              <li>Physical and Drug Test ($204)</li>
              <li>CPR and First Aid Certification ($75)</li>
              <li>Fingerprinting ($36.75)</li>
              <li>Health &amp; Safety Modules 1–4</li>
              <li>Child Abuse &amp; Neglect Training</li>
              <li>Safe Sleep 1 &amp; 2</li>
              <li>Universal Precautions</li>
              <li>Indiana Early Learning Foundations</li>
              <li>Orientation of the BBC Employee Handbook</li>
            </ul>
            <p className="italic text-gray-500 text-xs">
              Please note: All in-person pre-employment screenings (fingerprints, physical, and drug testing)
              must be completed within 24 hours of authorization to remain valid.
            </p>

            <h3 className="text-orange-600 mt-6">Onboarding Cost Agreement</h3>
            <p>
              Bright Beginnings invests in every team member's onboarding and training. By signing below,
              you authorize BBC to deduct the documented cost of the pre-employment expenses listed above
              from your wages if either of the following occurs:
            </p>
            <ul>
              <li>Your employment ends within the first 90 days, for any reason; or</li>
              <li>You resign without providing at least two weeks' written notice.</li>
            </ul>
            <p>
              Any such deduction will be made in compliance with applicable Indiana and federal wage law
              and will not reduce your pay below the legal minimum wage for the affected pay period. This
              authorization is voluntary and may be revoked by you in writing at any time; revocation does
              not waive amounts already owed. You may also repay these costs directly rather than by payroll
              deduction.
            </p>

            <h3 className="text-orange-600 mt-6">Orientation Period</h3>
            <p>
              You'll have a 90-day orientation period to learn our routines, values, and expectations while
              becoming part of the BBC family. After 90 days, you'll be eligible for earned Paid Time Off (PTO)
              and paid holidays.
            </p>
            <p>
              Welcome to the Bright Beginnings Family, where bright beginnings lead to even brighter futures.
              To accept this offer, please sign and date below.
            </p>

            <p className="mt-8">Warmly,</p>
            <p className="font-semibold mb-0">Ramal Taylor</p>
            <p className="text-gray-600 my-0">Founder &amp; Executive Director</p>
            <p className="text-gray-600 mt-0">Bright Beginnings Family Childcare</p>
          </div>

          {/* PDF download (if available) */}
          {data.offer_letter_url && (
            <div className="px-8 pb-6">
              <a href={data.offer_letter_url} target="_blank" rel="noreferrer"
                className="text-sm text-orange-600 hover:underline">
                Download PDF copy
              </a>
            </div>
          )}

          {/* Signing block */}
          <div className="bg-gray-50 border-t border-gray-100 p-6 space-y-5">
            {alreadySigned ? (
              <div className="flex items-center gap-3 text-green-700">
                <CheckCircleIcon className="w-6 h-6" />
                <div>
                  <p className="font-medium">You've already signed.</p>
                  <p className="text-xs text-gray-600">
                    Offer letter signed {new Date(data.offer_letter_signed_at).toLocaleString()}. Your policies are acknowledged separately, on your <a href="/my/policies" className="text-orange-600 hover:underline">Policies page</a>.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-orange-800 mb-1">Upload your state-issued ID *</p>
                  <p className="text-xs text-orange-700 mb-3">
                    We need a photo or scan of your driver's license or state ID before we can schedule your background check, drug screen, and fingerprinting. Accepted: JPG, PNG, or PDF.
                  </p>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={(e) => setIdFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-500 file:text-white hover:file:bg-orange-600 file:cursor-pointer"
                  />
                  {idFile && (
                    <p className="text-xs text-green-700 mt-2">
                      ✓ {idFile.name} ({Math.round(idFile.size / 1024)} KB)
                    </p>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  Type your full legal name (<strong>{expectedName}</strong>) in each box to sign. This counts as your legal signature.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Signature on offer letter
                  </label>
                  <input
                    value={offerSig}
                    onChange={(e) => setOfferSig(e.target.value)}
                    placeholder={`Type "${expectedName}"`}
                    className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:border-orange-500"
                  />
                </div>
                {/* THE HANDBOOK IS ACKNOWLEDGED ON ITS OWN PAGE. Retired from
                    this flow 2026-09-19.
                    
                    This block read `data.handbook_published`, which is computed
                    from handbook_versions — a table nothing writes to any more.
                    After six documents were published it still said "No handbook
                    has been published yet", contradicting the system of record on
                    a screen a new hire reads while signing.
                    
                    It also carried the defect step 8 removed everywhere else:
                    ONE WRITE PER FACT. Signing an offer letter and acknowledging
                    a handbook are two different claims about two different
                    documents, and one click should never make both. The
                    acknowledgment now lives at /my/policies with its own
                    version pins and its own record. */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700">Policies</p>
                  <p className="text-sm text-gray-600 mt-1">
                    The Staff Manual and the Attendance &amp; Scheduling Policy are
                    acknowledged separately, on your Policies page — each one records
                    the exact version you agreed to.{' '}
                    <strong>This does not hold up your offer.</strong>
                  </p>
                  <a href="/my/policies" className="text-xs text-orange-600 hover:underline mt-1 inline-block">
                    Open Policies →
                  </a>
                </div>
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  {submitting ? 'Signing…' : 'Sign & continue'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
